import { useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "@/app/hooks";
import { selectAuthActor } from "@/features/auth/authSessionSlice";
import {
  firstConversationShopCandidate,
  resolveConversationShopBindingId,
} from "@/features/chat/conversationShopScope";
import { useGetConversationHistoryInfiniteQuery } from "@/features/chat-history/conversationHistoryApi";
import { mergeConversationHistoryPages, toMerryChatActivityItem } from "@/features/chat-history/conversationHistoryModel";
import {
  useGetNotificationSummaryQuery,
} from "@/features/notifications/notificationApi";
import {
  type UserNotification,
} from "@/features/notifications/notificationSchemas";
import type {
  MerryCardMenuAction,
  MerryCardMenuCatalog,
} from "@/shared/ui/MerryCardMenu";
import {
  MerryChatHistoryDrawer,
  MerryChatNotificationStack,
} from "@/shared/ui/MerryChatActivity";
import {
  deriveMerryChatNavigationIndicator,
  type MerryChatActivityItem,
} from "@/shared/ui/merryChatActivityModel";
import { selectWorkspaceStoreSeedBindingId } from "@/features/settings/activeStoreSlice";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";

export interface UseWorkspaceChatActivityOptions {
  catalog: MerryCardMenuCatalog;
  onOpenNotificationDrawer: () => void;
  onNewChat: () => void;
  onOpenConversation: (conversationId: string, shopBindingId?: string | null) => void;
}

export function useWorkspaceChatActivity({
  catalog,
  onOpenNotificationDrawer,
  onNewChat,
  onOpenConversation,
}: UseWorkspaceChatActivityOptions) {
  const actor = useAppSelector(selectAuthActor);
  const organizationId = actor?.activeOrganizationId ?? "";
  const userId = actor?.userId ?? "";
  const selectedBindingId = useAppSelector((state) =>
    selectWorkspaceStoreSeedBindingId(state, organizationId),
  );
  const storeBindings = useGetMerchantStoreBindingsQuery(organizationId, {
    skip: !organizationId,
  });
  const shopBindingId = storeBindings.currentData
    ? resolveConversationShopBindingId(selectedBindingId, storeBindings.currentData.bindings)
    : null;
  const conversationsQuery = useGetConversationHistoryInfiniteQuery(
    { organizationId, query: "", shopBindingId: shopBindingId ?? "", userId, view: "active" },
    { skip: !organizationId || !userId || shopBindingId === null },
  );
  const notificationSummaryQuery = useGetNotificationSummaryQuery(
    organizationId,
    { pollingInterval: 60_000, skip: !organizationId || !userId },
  );
  const [dismissedThrough, setDismissedThrough] = useState("");
  const latestNotificationAtRef = useRef("");
  const notificationDeadlineRef = useRef(0);

  const historyItems = useMemo(
    () => mergeConversationHistoryPages(conversationsQuery.data?.pages),
    [conversationsQuery.data?.pages],
  );
  const conversationItems = useMemo<ReadonlyArray<MerryChatActivityItem>>(
    () => historyItems.map(toMerryChatActivityItem),
    [historyItems],
  );
  const shopByConversationId = useMemo(() => {
    const shops = new Map<string, string>();
    for (const item of historyItems) {
      const itemShopBindingId = firstConversationShopCandidate(item.shopBindingId);
      if (itemShopBindingId) shops.set(item.id, itemShopBindingId);
    }
    return shops;
  }, [historyItems]);

  const notifications = useMemo(
    () => notificationSummaryQuery.data?.items ?? [],
    [notificationSummaryQuery.data?.items],
  );
  const notificationItems = useMemo<ReadonlyArray<MerryChatActivityItem>>(
    () => notifications.map(toActivityItem),
    [notifications],
  );

  const historyIndicator = useMemo(
    () => deriveMerryChatNavigationIndicator(conversationItems),
    [conversationItems],
  );
  const unreadCount = notificationSummaryQuery.data?.unreadCount ?? 0;
  const notificationIndicator =
    unreadCount > 1
      ? ({ count: unreadCount, type: "count" } as const)
      : unreadCount === 1
        ? ({ type: "dot" } as const)
        : ({ type: "none" } as const);
  const newestNotificationAt = notifications.reduce(
    (latest, item) => item.occurredAt > latest ? item.occurredAt : latest,
    "",
  );
  const isNotificationVisible = newestNotificationAt > dismissedThrough;
  useEffect(() => {
    if (!newestNotificationAt) return;
    if (newestNotificationAt > latestNotificationAtRef.current) {
      latestNotificationAtRef.current = newestNotificationAt;
      notificationDeadlineRef.current = Date.now() + 4_500;
    }
    const timer = window.setTimeout(
      () => setDismissedThrough(latestNotificationAtRef.current),
      Math.max(0, notificationDeadlineRef.current - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [newestNotificationAt]);

  const activityCatalog = useMemo(
    () =>
      ({
        ...catalog,
        chatHistory: {
          ...catalog.chatHistory,
          sections:
            conversationItems.length > 0
              ? conversationItems.length > 3
                ? [
                    {
                      id: "recent",
                      items: conversationItems.slice(0, 3),
                      label: "最近聊天",
                    },
                    {
                      id: "earlier",
                      items: conversationItems.slice(3),
                      label: "更早",
                    },
                  ]
                : [
                    {
                      id: "recent",
                      items: conversationItems,
                      label: "最近聊天",
                    },
                  ]
              : [],
        },
      }) satisfies MerryCardMenuCatalog,
    [catalog, conversationItems],
  );

  const handleHistoryAction = (action: MerryCardMenuAction) => {
    if (action.id === "new-chat") {
      onNewChat();
      return;
    }
    onOpenConversation(action.id, shopByConversationId.get(action.id));
  };

  return {
    chatHistoryDrawer: (
      <MerryChatHistoryDrawer
        catalog={activityCatalog}
        onAction={handleHistoryAction}
      />
    ),
    chatHistoryIndicator: historyIndicator,
    notificationIndicator,
    notificationOverlay:
      isNotificationVisible && notificationItems.length > 0 ? (
      <MerryChatNotificationStack
        items={notificationItems}
        onOpenNotification={onOpenNotificationDrawer}
      />
      ) : null,
  };
}

function toActivityItem(notification: UserNotification): MerryChatActivityItem {
  return {
    id: notification.id,
    preview: notification.summary,
    status: notification.readAt ? "complete" : "dot",
    time: conversationTime(notification.occurredAt),
    title: notification.title,
  };
}

function conversationTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat("zh-CN", {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
    }).format(date);
  }
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "numeric",
  }).format(date);
}
